import { useCallback, useContext, useMemo } from 'react';
import _ from 'underscore';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { AdFormat } from '@bingads-webui-campaign-react/display-plus-native-ads';
import { useWizardService } from '@bingads-webui-react/wizard';

import { useMultipleNativeAdsRepository, getDefaultCampaign, getDefaultAdGroup, useNativeAdRepository } from '../native-ads';
import { useDisplayAdsRepository } from '../display-ads';
import { useVideoAdsRepository } from '../video-ads';
import { useSelectedAdFormatState } from '../../common/use-selected-ad-format';

export const useMultiAdFormatRepository = (isMultipleFlow = true) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);

  const defaultCampaign = useMemo(() => getDefaultCampaign(i18n, serverContext), [i18n, serverContext]);
  const defaultAdGroup = useMemo(() => getDefaultAdGroup(i18n, serverContext), [i18n, serverContext]);

  const { state } = useWizardService();
  const [selectedAdFormat] = useSelectedAdFormatState();

  const { save: nativeAdSave } = useNativeAdRepository();
  const { save: multipleNativeAdSave } = useMultipleNativeAdsRepository();
  const { save: displayAdSave } = useDisplayAdsRepository(defaultCampaign, defaultAdGroup);
  const { save: videoAdSave } = useVideoAdsRepository(defaultCampaign, defaultAdGroup);

  const save = useCallback((data) => {
    const dataToSave = { ...data, data: { ...data.data, adSubType: selectedAdFormat } };
    switch (selectedAdFormat) {
      case AdFormat.Native:
        return isMultipleFlow ? multipleNativeAdSave(dataToSave) : nativeAdSave(dataToSave);
      case AdFormat.Display:
        return displayAdSave(dataToSave);
      case AdFormat.Video:
        return videoAdSave(dataToSave);
      default:
        return Promise.reject();
    }
  }, [selectedAdFormat, isMultipleFlow, multipleNativeAdSave, nativeAdSave, displayAdSave, videoAdSave]);

  const load = useCallback(() => new Promise((resolve) => {
    if (state.context.wizardData.ads) {
      const ids = _.keys(state.context.wizardData.ads.ads);
      if (!isMultipleFlow && ids.length > 0) {
        resolve(state.context.wizardData.ads.ads[ids[0]]);
      } else if (state.context.wizardData.editingAdId) {
        const { editingAdId } = state.context.wizardData;
        resolve(state.context.wizardData.ads.ads[editingAdId]);
      } else {
        resolve(undefined);
      }
    } else {
      resolve(undefined);
    }
  }), [isMultipleFlow, state.context.wizardData]);

  return { save, load };
};

export const useSingleMultiAdFormatRepository = () => useMultiAdFormatRepository(false);
